import React, { ReactNode } from 'react';
import { Text } from '@mofid-securities/uikit';
import {
  Controller,
  ControllerProps,
  FieldError,
  FieldPath,
  FieldValues,
  useFormContext,
  UseFormReturn,
} from 'react-hook-form';
import { FormUnderlinedText } from '@/components/UnderlinedText';
import { classnames } from '@/common/utils/classnames';

export interface IFormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  render: (
    __args: Parameters<ControllerProps<TFieldValues, TName>['render']>[number] &
      UseFormReturn<TFieldValues>
  ) => ReturnType<ControllerProps<TFieldValues, TName>['render']>;
  errorHandler?: (__error: FieldError) => ReactNode;
  className?: string;
  classNames?: {
    root?: string;
    error?: string;
    formFieldRoot?: string;
  };
  label?: string;
  additionalContent?: React.ReactNode;
  isFullWidth?: boolean;
  shouldShowError?: boolean;
  isRequired?: boolean;
}

const FormField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  label,
  name,
  render,
  className = '',
  classNames,
  errorHandler,
  additionalContent,
  isFullWidth,
  shouldShowError = true,
  isRequired = false,
}: IFormFieldProps<TFieldValues, TName>): JSX.Element => {
  const form = useFormContext<TFieldValues>();

  const isFormFieldHeaderVisible = additionalContent || label;

  return (
    <div
      className={classnames(
        'flex flex-col gap-y-2',
        { 'w-full': isFullWidth },
        classNames?.formFieldRoot
      )}
    >
      {isFormFieldHeaderVisible && (
        <div className="flex justify-between">
          {label && (
            <Text
              className="inline-flex gap-x-1 pr-2 text-gray-filled-primary-enabled"
              component="label"
              htmlFor={name}
              size="xs"
            >
              {label}
              {isRequired && (
                <span className="text-error-filled-primary-enabled">*</span>
              )}
            </Text>
          )}
          {additionalContent}
        </div>
      )}
      <Controller<TFieldValues>
        control={form.control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const { error } = fieldState;
          const errorNode: ReactNode =
            error && errorHandler ? errorHandler?.(error) : getErrorNode(error);
          return (
            <div className={`${className} ${classNames?.root || ''}`}>
              {/* @ts-expect-error */}
              {render({ field, fieldState, formState, ...form })}
              {!!errorNode && shouldShowError && (
                <div
                  className={`mt-[2px] text-xs text-error ${
                    classNames?.error || ''
                  }`}
                >
                  {errorNode}
                </div>
              )}
            </div>
          );
        }}
      />
    </div>
  );
};

function getErrorNode(error: FieldError | undefined): ReactNode {
  if (error && (Object.values(error)?.[0] as any)?.type) {
    return getErrorNode(Object.values(error)?.[0] as FieldError);
    // return Object.values(error).map((v, i, array) => (
    //   <Fragment key={i}>
    //     {getErrorNode(v as FieldError)}
    //     {i + 1 < array.length && <br />}
    //   </Fragment>
    // ));
  }

  const messageObj: any = error?.message as any;

  if (!error || !messageObj) {
    return null;
  }

  if (typeof messageObj === 'string') {
    return messageObj;
  }

  const {
    message,
    placeholder,
    value,
    separator = '${placeholder}',
  } = messageObj || {};

  if (typeof message === 'string') {
    return (
      <>
        {(message?.split(separator) as Array<string>).reduce(
          (acc: ReactNode[], current, index, array) =>
            acc.concat([
              current,
              array.length > index + 1 && (
                <FormUnderlinedText
                  key={index}
                  className="border-error"
                  name={error?.ref?.name as string}
                  value={value ?? placeholder}
                >
                  {placeholder ?? value}
                </FormUnderlinedText>
              ),
            ]),
          []
        )}
      </>
    );
  }

  return null;
}

export default FormField;
